// // CurvesPreview.jsx
// import React, { useMemo } from "react";

// const clamp = (x, min, max) => Math.min(max, Math.max(min, x));

// export default function CurvesPreview({
//   shadows = 0,
//   midtones = 0,
//   highlights = 0,
//   width = 280,
//   height = 160,
//   className = "",
// }) {
//   const { poly, gridLines } = useMemo(() => {
//     const s = clamp(Number(shadows) || 0, -1, 1);
//     const m = clamp(Number(midtones) || 0, -1, 1);
//     const h = clamp(Number(highlights) || 0, -1, 1);

//     // ---- SAME MATH as in applyCurvesFilters ----
//     // Shadows: lift/crush + contrast tweak
//     const b_shadows = s * 0.25;
//     const c_shadows = 1 - Math.sign(s) * Math.abs(s) * 0.2;

//     // Highlights: contrast tweak + slight opposite brightness
//     const c_highs = 1 + h * 0.5;
//     const b_highs = -Math.sign(h) * Math.abs(h) * 0.1;

//     const c_total = clamp((c_shadows + c_highs) / 2, 0.5, 1.5);
//     const b_total = clamp(b_shadows + b_highs, -0.3, 0.3);

//     // Midtones via gamma
//     const g_mid = clamp(1 - m * 0.6, 0.2, 3);

//     // Sample the curve: x in [0..1] → y in [0..1]
//     const N = 64;
//     const pts = [];
//     for (let i = 0; i <= N; i++) {
//       const x = i / N; // input luminance
//       // gamma mapping around midtones
//       let y = Math.pow(x, g_mid);
//       // apply contrast + brightness from shadows/highlights blend
//       y = clamp(y * c_total + b_total, 0, 1);

//       // map to SVG coordinates (0,0 top-left)
//       const px = x * width;
//       const py = (1 - y) * height; // invert so 1.0 is top
//       pts.push([px, py]);
//     }

//     // grid: 4x4
//     const g = [];
//     for (let i = 1; i < 4; i++) {
//       const x = (i / 4) * width;
//       g.push({ x1: x, y1: 0, x2: x, y2: height });
//       const y = (i / 4) * height;
//       g.push({ x1: 0, y1: y, x2: width, y2: y });
//     }

//     return {
//       poly: pts.map(([x, y]) => `${x},${y}`).join(" "),
//       gridLines: g,
//     };
//   }, [shadows, midtones, highlights, width, height]);

//   return (
//     <svg
//       width={width}
//       height={height}
//       className={className}
//       viewBox={`0 0 ${width} ${height}`}
//       fill="none"
//     >
//       {/* background */}
//       <rect
//         x="0"
//         y="0"
//         width={width}
//         height={height}
//         rx="8"
//         fill="#ffffff"
//         stroke="#EDEFF2"
//       />

//       {/* grid */}
//       {gridLines.map((l, idx) => (
//         <line
//           key={idx}
//           x1={l.x1}
//           y1={l.y1}
//           x2={l.x2}
//           y2={l.y2}
//           stroke="#E9EEF4"
//           strokeWidth="1"
//         />
//       ))}

//       {/* base diagonal (identity) */}
//       <line
//         x1="0"
//         y1={height}
//         x2={width}
//         y2="0"
//         stroke="#C9D3DF"
//         strokeWidth="2"
//         strokeDasharray="4 4"
//       />

//       {/* sampled curve */}
//       <polyline
//         points={poly}
//         fill="none"
//         stroke="#6B9FF8"
//         strokeWidth="4"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

// CurvesPreview.jsx
import React, { useMemo } from "react";

/**
 * Small helper: clamp number to [min, max]
 */
const clamp = (x, min, max) => Math.min(max, Math.max(min, x));

/**
 * Tone mapping function driven by three intuitive controls.
 *  - shadows   ∈ [-1, 1]  → affects darks (x small)
 *  - midtones  ∈ [-1, 1]  → affects mid-range (x ≈ 0.5)
 *  - highlights∈ [-1, 1]  → affects brights (x large)
 *
 * It blends simple basis functions so you get a smooth, monotonic curve.
 */
function makeTone(shadows = 0, midtones = 0, highlights = 0) {
  const s = clamp(shadows, -1, 1);
  const m = clamp(midtones, -1, 1);
  const h = clamp(highlights, -1, 1);

  return (x) => {
    // basis terms
    const x2 = x * x;
    const oneMinusX = 1 - x;

    // shadows: strong near 0
    const shadowTerm = oneMinusX * oneMinusX * 0.6 * s;

    // midtones: bell-ish around 0.5 => 4x(1-x) in [0..1], centered
    const midTerm = (4 * x * oneMinusX - 1) * 0.25 * m;

    // highlights: strong near 1
    const highlightTerm = x2 * 0.6 * h;

    // base is identity line
    const y = x + shadowTerm + midTerm + highlightTerm;

    return clamp(y, 0, 1);
  };
}

/**
 * Convert sampled points → a smooth cubic Bézier path using a Catmull-Rom → Bézier conversion.
 * pts: array of [x, y] in "math" coords (0..1)
 * tension: how curvy the line is (0.2–0.35 feels good)
 */
function pointsToBezierPath(pts, tension = 0.25) {
  if (!pts?.length) return "";
  const p = pts.map(([x, y]) => ({ x, y }));
  const n = p.length;
  if (n === 1) return `M ${p[0].x} ${p[0].y}`;
  if (n === 2) return `M ${p[0].x} ${p[0].y} L ${p[1].x} ${p[1].y}`;

  let d = `M ${p[0].x} ${p[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = p[Math.max(0, i - 1)];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[Math.min(n - 1, i + 2)];

    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

/**
 * CurvesPreview
 * Props:
 *  - width, height: svg size
 *  - shadows, midtones, highlights: [-1,1]
 *  - grid: show background grid
 */
export default function CurvesPreview({
  width = 280,
  height = 160,
  shadows = 0,
  midtones = 0,
  highlights = 0,
  grid = true,
}) {
  const PAD = 1; // inner padding
  const W = width - PAD * 2;
  const H = height - PAD * 2;

  // Build the tone curve sampler
  const tone = useMemo(
    () => makeTone(shadows, midtones, highlights),
    [shadows, midtones, highlights]
  );

  // Sample points in math coords (0..1), then map to SVG coords
  const { pathD, diag, gridLines } = useMemo(() => {
    // math→svg mapping
    const sx = (x) => PAD + x * W;
    const sy = (y) => PAD + (1 - y) * H; // invert Y for SVG

    // sample curve
    const N = 64;
    const pts = Array.from({ length: N }, (_, i) => {
      const x = i / (N - 1);
      const y = tone(x);
      return [sx(x), sy(y)];
    });

    // diagonal reference line (identity)
    const diagLine = `M ${sx(0)} ${sy(0)} L ${sx(1)} ${sy(1)}`;

    // light grid (4×4)
    const gl = [];
    for (let i = 1; i < 4; i++) {
      const gx = sx(i / 4);
      const gy = sy(i / 4);
      gl.push(
        // vertical
        <line
          key={`gv${i}`}
          x1={gx}
          y1={PAD}
          x2={gx}
          y2={PAD + H}
          stroke="#EAEFF6"
          strokeWidth="1"
        />,
        // horizontal
        <line
          key={`gh${i}`}
          x1={PAD}
          y1={gy}
          x2={PAD + W}
          y2={gy}
          stroke="#EAEFF6"
          strokeWidth="1"
        />
      );
    }

    // smooth path from sampled pts
    const d = pointsToBezierPath(pts, 0.25);

    return { pathD: d, diag: diagLine, gridLines: gl };
  }, [W, H, PAD, tone]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Curves adjustment preview"
      style={{
        display: "block",
        background: "#fff",
        borderRadius: 8,
        border: "1px solid #EFEFEF",
      }}
    >
      {/* Frame */}
      <rect
        x={PAD}
        y={PAD}
        width={W}
        height={H}
        rx="6"
        fill="#FFFFFF"
        stroke="#EDF0F3"
      />

      {/* Grid */}
      {grid && gridLines}

      {/* Diagonal reference */}
      <path d={diag} stroke="#CBD5E1" strokeWidth="2" fill="none" />

      {/* Smooth curve */}
      <path
        d={pathD}
        fill="none"
        stroke="#949494"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Optional: end handles to hint control (purely visual) */}
      <circle cx={PAD + W * 0.22} cy={PAD + H * 0.78} r="7" fill="#949494" />
      <circle cx={PAD + W * 0.78} cy={PAD + H * 0.22} r="7" fill="#949494" />
    </svg>
  );
}
